import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { useDIDStore } from '../stores/did-store';
import { useReleaseDID } from '../hooks';
import { formatPhoneNumber } from '@/lib/utils';
import { TriangleAlert } from 'lucide-react';

/**
 * Standalone disconnect confirmation modal.
 * Can be triggered from anywhere using useDIDStore().openDisconnectModal(tn)
 */
export function DisconnectDIDModal() {
  const { disconnectDID, isDisconnectModalOpen, closeDisconnectModal } = useDIDStore();
  const releaseDID = useReleaseDID();
  const [confirmText, setConfirmText] = useState('');

  // Reset confirm text when modal closes
  useEffect(() => {
    if (!isDisconnectModalOpen) {
      setConfirmText('');
    }
  }, [isDisconnectModalOpen]);

  if (!isDisconnectModalOpen || !disconnectDID) return null;

  const handleRelease = () => {
    releaseDID.mutate(disconnectDID, {
      onSuccess: () => {
        closeDisconnectModal();
      },
    });
  };

  const canConfirmRelease = confirmText === 'DISCONNECT';

  return (
    <Modal
      isOpen={isDisconnectModalOpen}
      onClose={() => {
        if (!releaseDID.isPending) {
          closeDisconnectModal();
        }
      }}
      title="Disconnect DID"
      size="md"
    >
      <div className="space-y-4">
        {/* Strong Warning */}
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <TriangleAlert className="h-6 w-6 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-red-800">
              WARNING: This action cannot be undone!
            </p>
            <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
              <li>This phone number will be returned to the carrier</li>
              <li>All configuration (forwarding, E911, etc.) will be lost</li>
              <li>The number may be assigned to another customer</li>
              <li>You will not be able to recover this number</li>
            </ul>
          </div>
        </div>

        {/* DID Info */}
        <div className="p-4 bg-slate-50 rounded-lg">
          <p className="text-sm text-slate-600">Phone number to disconnect:</p>
          <p className="text-xl font-bold text-slate-900 font-mono mt-1">
            {formatPhoneNumber(disconnectDID)}
          </p>
        </div>

        {/* Confirmation Input */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Type <span className="font-bold text-red-600">DISCONNECT</span> to confirm:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
            placeholder="DISCONNECT"
            disabled={releaseDID.isPending}
          />
        </div>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeDisconnectModal}
            disabled={releaseDID.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleRelease}
            disabled={!canConfirmRelease}
            isLoading={releaseDID.isPending}
          >
            Disconnect DID Permanently
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
}
